import type { ColumnDef } from '@tanstack/react-table'
import { ArrowUpDown, MoreHorizontal, Eye, MessageCircle, AlertOctagon, AlertTriangle, CheckCircle2, UserMinus } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import type { RedemptionPatientRow, RedemptionRiskTier } from '@/lib/api-client'
import { isValidWhatsAppNumber, getWhatsAppUrl } from '@/lib/whatsapp'

const RISK_META: Record<RedemptionRiskTier, { label: string; icon: typeof AlertOctagon; bg: string; text: string }> = {
  critical: { label: 'Critical', icon: AlertOctagon, bg: '#ef4444', text: 'white' },
  warning:  { label: 'Warning',  icon: AlertTriangle, bg: '#f59e0b', text: 'white' },
  active:   { label: 'Active',   icon: CheckCircle2, bg: '#10b981', text: 'white' },
  churn:    { label: 'Churn',    icon: UserMinus, bg: '#525860', text: '#e2e8f0' },
}

function formatLastVisit(iso: string | null, days: number): { date: string; rel: string } {
  if (!iso) return { date: '—', rel: '—' }
  const d = new Date(iso)
  const date = d.toLocaleDateString('en-MY', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    timeZone: 'Asia/Kuala_Lumpur',
  })
  const rel = days <= 1 ? 'today' : days < 30 ? `${days}d ago` : days < 365 ? `${Math.round(days / 30)}mo ago` : `${(days / 365).toFixed(1)}y ago`
  return { date, rel }
}

interface ColumnsProps {
  onView: (row: RedemptionPatientRow) => void
}

export function getRedemptionColumns({ onView }: ColumnsProps): ColumnDef<RedemptionPatientRow>[] {
  return [
    {
      id: 'risk',
      header: 'Risk',
      cell: ({ row }) => {
        const tier = row.original.riskTier
        const meta = RISK_META[tier]
        const Icon = meta.icon
        return (
          <Badge style={{ backgroundColor: meta.bg, color: meta.text }} className="border-0 font-medium gap-1">
            <Icon className="w-3 h-3" />
            {meta.label}
          </Badge>
        )
      },
      enableSorting: false,
    },
    {
      accessorKey: 'name',
      header: ({ column }) => (
        <Button
          variant="ghost"
          className="font-semibold px-0 hover:bg-transparent"
          onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
        >
          Patient
          <ArrowUpDown className="ml-2 h-4 w-4" />
        </Button>
      ),
      cell: ({ row }) => (
        <div className="min-w-0">
          <div className="font-medium truncate max-w-[240px]" title={row.original.name}>
            {row.original.name}
          </div>
          <div className="text-xs text-text-muted">{row.original.pno} · {row.original.branchName ?? '—'}</div>
        </div>
      ),
    },
    {
      accessorKey: 'lastVisit',
      header: ({ column }) => (
        <Button
          variant="ghost"
          className="font-semibold px-0 hover:bg-transparent"
          onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
        >
          Last Visit
          <ArrowUpDown className="ml-2 h-4 w-4" />
        </Button>
      ),
      cell: ({ row }) => {
        const { date, rel } = formatLastVisit(row.original.lastVisit, row.original.daysSinceVisit)
        return (
          <div>
            <div className="font-medium text-sm">{date}</div>
            <div className="text-xs text-text-muted">{rel}</div>
          </div>
        )
      },
    },
    {
      accessorKey: 'outstanding',
      header: ({ column }) => (
        <Button
          variant="ghost"
          className="font-semibold px-0 hover:bg-transparent"
          onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
        >
          Outstanding
          <ArrowUpDown className="ml-2 h-4 w-4" />
        </Button>
      ),
      cell: ({ row }) => (
        <div className="font-mono">
          <span className="text-lg font-semibold">{row.original.outstanding}</span>
          <span className="text-xs text-text-muted ml-1">sessions</span>
        </div>
      ),
    },
    {
      id: 'actions',
      header: '',
      cell: ({ row }) => {
        const r = row.original
        const phoneValid = r.phone && isValidWhatsAppNumber(r.phone)
        return (
          <div className="flex items-center justify-end gap-1">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" className="h-8 w-8">
                  <MoreHorizontal className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuLabel>Actions</DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => onView(r)}>
                  <Eye className="mr-2 h-4 w-4" />
                  View detail
                </DropdownMenuItem>
                {phoneValid && (
                  <DropdownMenuItem asChild>
                    <a
                      href={getWhatsAppUrl(r.phone!)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center"
                    >
                      <MessageCircle className="mr-2 h-4 w-4" />
                      WhatsApp patient
                    </a>
                  </DropdownMenuItem>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        )
      },
      enableSorting: false,
    },
  ]
}
